import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import profile from "../assets/profile.png";
import verified from "../assets/gold-verified-1.png";
import star from "../assets/Star_icon.png";
import api from "../Api.js";
import './TechProfile.css';
import { getAuthenticatedSessionToken, getUserSessionToken } from '../utils/auth';

const WorkGalleryDescription = ({ text }) => {
  const descriptionRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const description = descriptionRef.current;
    if (!description || expanded) return undefined;

    const measureDescription = () => {
      setHasMore(description.scrollHeight > description.clientHeight + 1);
    };

    measureDescription();
    window.addEventListener('resize', measureDescription);
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measureDescription)
      : null;
    resizeObserver?.observe(description);

    return () => {
      window.removeEventListener('resize', measureDescription);
      resizeObserver?.disconnect();
    };
  }, [text, expanded]);

  return (
    <div className={`tp-post-description${expanded ? ' expanded' : ''}`}>
      <p className="tp-post-text" ref={descriptionRef}>{text}</p>
      {(hasMore || expanded) && (
        <button
          type="button"
          className="tp-post-description-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((isExpanded) => !isExpanded)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const TechProfile = () => {
  const { id } = useParams();
  const [technician, setTechnician] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('details');

  // Fetch technician data from API
  useEffect(() => {
    const userToken = getAuthenticatedSessionToken();
    if (!userToken) {
      navigate('/login?tab=user', { state: { from: `/tech-profile/${id}` } });  
      return undefined;
    }

    let cancelled = false;
    const fetchTechnician = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/api/web/get-technician/${id}`, {
          headers: {
            accept: 'application/json',
            Authorization: `Bearer ${userToken}`
          }
        });
        if (response.data.success && response.data.data) {
          const techData = response.data.data;

          // Extract categories, subcategories, and childcategories
          const categories = techData.category || [];
          const subcategories = techData.subcategory || [];
          const childcategories = techData.childcategory || [];

          // Format skills from categories and subcategories
          const skills = [];
          
          // Add category names
          categories.forEach(cat => {
            if (cat.categoryName) {
              skills.push(cat.categoryName);
            }
          });
          
          // Add subcategory names
          subcategories.forEach(sub => {
            if (sub.subCategoryName) {
              skills.push(sub.subCategoryName);
            }
          });
          
          // Add child category names
          childcategories.forEach(child => {
            if (child.childCategoryName) {
              skills.push(child.childCategoryName);
            }
          });

          // Format reviews from API
const formattedReviews = (techData.reviews || []).map((review, index) => {
  const user = review.user || {};

  // Pick a single display rating (prefer overallRating, then average of sub-ratings)
  const subRatings = [
    Number(review.workQualityRating) || 0,
    Number(review.behaviorRating) || 0,
    Number(review.personalityRating) || 0
  ].filter((r) => r > 0);
  const computedAverage = subRatings.length
    ? subRatings.reduce((a, b) => a + b, 0) / subRatings.length
    : 0;

  return {
    id: review._id || index + 1,
    rating: Number(review.overallRating) || computedAverage || 0,
    text: review.experience || review.comment || review.text || '',
    userName: user.user_name || user.name || review.userName || 'Anonymous',
    userImage: user.profile_image
      ? (user.profile_image.startsWith('http')
          ? user.profile_image
          : `https://api.oneclickservice.in${user.profile_image}`)
      : null,
    createdAt: review.createdAt || null,
    // keep detailed ratings in case you want to display them
    workQualityRating: Number(review.workQualityRating) || 0,
    behaviorRating: Number(review.behaviorRating) || 0,
    personalityRating: Number(review.personalityRating) || 0,
    wouldRecommend: !!review.wouldRecommend,
    pricingFair: !!review.pricingFair
  };
});

          const galleryItems = Array.isArray(techData.work_gallery)
            ? techData.work_gallery
            : Array.isArray(techData.posts) ? techData.posts : [];
          const formattedPosts = galleryItems.map((post, index) => {
            const postDate = post.work_date || post.createdAt;
            const parsedDate = postDate ? new Date(postDate) : null;
            const validDate = parsedDate && !Number.isNaN(parsedDate.getTime());
            const getMediaUrl = (media) => typeof media === 'string'
              ? media
              : media?.url || media?.src || '';
            const toMediaArray = (value) => Array.isArray(value) ? value : value ? [value] : [];
            const images = toMediaArray(post.work_images || post.images)
              .map(getMediaUrl)
              .filter(Boolean);
            const videos = toMediaArray(post.work_videos || post.videos)
              .map(getMediaUrl)
              .filter(Boolean);

            return {
              id: post._id || index + 1,
              title: post.title || '',
              date: validDate ? parsedDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : '',
              time: validDate && post.createdAt ? new Date(post.createdAt).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              }) : '',
              text: post.description || post.text || '',
              media: [
                ...images.map((url) => ({ type: 'image', url })),
                ...videos.map((url) => ({ type: 'video', url }))
              ]
            };
          });

          const formattedTechnician = {
            _id: techData._id,
            technician_id: techData.technician_id,
            name: techData.name || 'Technician',
            rating: Number(techData.rating?.averageRating) || 0,
            totalReviews: techData.rating?.totalReviews || 0,
            workQualityRating: techData.rating?.workQualityRating || 0,
            behaviorRating: techData.rating?.behaviorRating || 0,
            personalityRating: techData.rating?.personalityRating || 0,
            location: [techData.city, techData.address].filter(Boolean).join(', '),
            jobsDone: techData.jobs_completed ?? null,
            responseTime: techData.response_time || null,
            distance: techData.distance || null,
            phone: techData.mobile || '',
            profileImage: techData.profile_pic || profile,
            about: techData.about_us || '',
            skills: skills,
            categories: categories,
            subcategories: subcategories,
            childcategories: childcategories,
            verified: techData.confirm_status === 'UnBlocked',
            status: techData.status,
            subscription_status: techData.subscription_status,
            availability_status: techData.availability_status,
            confirm_status: techData.confirm_status,
            kyc_status: techData.kyc_status,
            email: techData.email,
            mobile: techData.mobile,
            address: techData.address,
            city: techData.city,
            zip_code: techData.zip_code,
            blood_group: techData.blood_group,
            createdAt: techData.createdAt,
            updatedAt: techData.updatedAt,
            reviews: formattedReviews,
            posts: formattedPosts
          };

          if (!cancelled) {
            setTechnician(formattedTechnician);
            setReviews(formattedReviews);
            setPosts(formattedPosts);
          }
        } else {
          throw new Error('Technician information is unavailable.');
        }
      } catch (err) {
        console.error('Error fetching technician:', err);
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load technician data.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTechnician();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handlecall = async () => {
    try {
      const token = getUserSessionToken();

      if (!token) {
        navigate("/login?tab=user", { state: { from: `/tech-profile/${id}` } });
        return;
      }

      const technicianId = id;

      const response = await api.post(
        "/api/web/technicians/view-create-log",
        {
          technicianId: technicianId
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        window.location.href = `tel:${technician?.mobile || technician?.phone}`;
      } else {
        setError("Please log in with a user account to view technician details.");
      }
    } catch (error) {
      console.error("Profile view error:", error);

      if (error.response?.status === 401) {
        alert("Session expired. Please log in again.");
        navigate("/login?tab=user", { state: { from: `/tech-profile/${id}` } });
      } else {
        alert(
          error.response?.data?.message || "Something went wrong. Please try again."
        );
      }
    }
  };

  const getMediaGridClass = (mediaCount) => {
    return `tp-post-images count-${Math.min(mediaCount, 5)}`;
  };

  // Helper function to render star rating
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    let stars = [];
    for (let i = 0; i < fullStars; i++) {
      stars.push('★');
    }
    if (hasHalfStar) {
      stars.push('⭐');
    }
    for (let i = 0; i < emptyStars; i++) {
      stars.push('☆');
    }
    return stars.join('');
  };

  if (loading) {
    return (
      <main>
        <section className="tp-section">
          <div className="tp-loading">
            <div className="tp-spinner"></div>
            <p className="tp-loading-text">Loading technician profile...</p>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <section className="tp-section">
          <div className="tp-error">
            <p className="tp-error-msg">{error}</p>
            <button onClick={() => window.location.reload()} className="tp-retry-btn">
              Try Again
            </button>
          </div>
        </section>
      </main>
    );
  }

  if (!technician) {
    return (
      <main>
        <section className="tp-section">
          <div className="tp-error">
            <p className="tp-error-msg">No technician data available</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="tp-section">
        {/* LEFT SIDE */}
        <div className="tp-left">
          <div className="tp-profile-card">
            <div className="tp-profile-image-wrapper">
              <img
                src={technician.profileImage}
                alt={technician.name}
                className="tp-profile-image"
                onError={(e) => { e.target.src = profile; }}
              />
              {technician.availability_status === 'Available' && (
                <span className="tp-availability-badge">● Available</span>
              )}
            </div>

            <h2 className="tp-name">
              {technician.name}
              {technician.verified && (
                <span className="tp-verified-badge">
                  <img src={verified} alt="verified" className="tp-verified-icon" />
                  Verified
                </span>
              )}
            </h2>

            <p className="tp-rating">
              <img src={star} alt="star" className="tp-star-icon" />
              <span className="tp-rating-value">{technician.rating.toFixed(1)}</span>
              <span className="tp-rating-count">({technician.totalReviews} reviews)</span>
              {technician.rating > 0 && (
                <span className="tp-rating-stars"> {renderStars(technician.rating)}</span>
              )}
            </p>

            <p className="tp-location">
              📍 {technician.location}
            </p>

            <div className="tp-stats">
              <div className="tp-stat-item">
                <strong className="tp-stat-value">{technician.jobsDone ?? '—'}</strong>
                <span className="tp-stat-label">Jobs Done</span>
              </div>
              <div className="tp-stat-item">
                <strong className="tp-stat-value">{technician.responseTime || '—'}</strong>
                <span className="tp-stat-label">Response</span>
              </div>
              <div className="tp-stat-item">
                <strong className="tp-stat-value">{technician.distance || '—'}</strong>
                <span className="tp-stat-label">Distance</span>
              </div>
            </div>

            <button className="tp-call-btn" onClick={handlecall} style={{color:"white"}}>
              <span className="ts-call-icon">📞</span> <span style={{color:"white"}}>Call Now</span>
            </button>
          </div>

          <nav className="tp-profile-nav" aria-label="Technician profile sections">
            <button
              type="button"
              className={`tp-profile-nav-item${activeTab === 'details' ? ' active' : ''}`}
              aria-current={activeTab === 'details' ? 'page' : undefined}
              onClick={() => setActiveTab('details')}
            >
              <span className="tp-profile-nav-icon" aria-hidden="true">👤</span>
              <span>Profile details</span>
              <span className="tp-profile-nav-arrow" aria-hidden="true">›</span>
            </button>
            <button
              type="button"
              className={`tp-profile-nav-item${activeTab === 'work' ? ' active' : ''}`}
              aria-current={activeTab === 'work' ? 'page' : undefined}
              onClick={() => setActiveTab('work')}
            >
              <span className="tp-profile-nav-icon" aria-hidden="true">🛠</span>
              <span>Recent work</span>
              <span className="tp-profile-nav-count">{posts.length}</span>
              <span className="tp-profile-nav-arrow" aria-hidden="true">›</span>
            </button>
          </nav>
        </div>

        <div className="tp-right">
          {activeTab === 'details' ? (
            <div className="tp-details-content">
              <div className="tp-content-heading">
                <span className="tp-content-eyebrow">Technician profile</span>
                <h1>Profile details</h1>
                <p>Learn more about {technician.name} and their expertise.</p>
              </div>
              <div className="tp-section-card">
                <h3 className="tp-section-title">About</h3>
                <p className="tp-about-text">{technician.about || 'No profile description available.'}</p>
                {technician.email && (
                  <p className="tp-contact-info"><strong>Email:</strong> {technician.email}</p>
                )}
                {technician.blood_group && (
                  <p className="tp-contact-info"><strong>Blood Group:</strong> {technician.blood_group}</p>
                )}
                {technician.address && (
                  <p className="tp-contact-info">
                    <strong>Address:</strong> {[technician.address, technician.city, technician.zip_code].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              <div className="tp-section-card">
                <h3 className="tp-section-title">Skills & Expertise</h3>
                <div className="tp-skills-container">
                  {technician.categories?.length > 0 && (
                    <div className="tp-skill-group">
                      <h4 className="tp-skill-group-title">Categories</h4>
                      <div className="tp-skills">
                        {technician.categories.map((cat, index) => (
                          <span key={`cat-${index}`} className="tp-skill-tag tp-category-tag">{cat.categoryName}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {technician.subcategories?.length > 0 && (
                    <div className="tp-skill-group">
                      <h4 className="tp-skill-group-title">Subcategories</h4>
                      <div className="tp-skills">
                        {technician.subcategories.map((sub, index) => (
                          <span key={`sub-${index}`} className="tp-skill-tag tp-subcategory-tag">{sub.subCategoryName}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {technician.childcategories?.length > 0 && (
                    <div className="tp-skill-group">
                      <h4 className="tp-skill-group-title">Specializations</h4>
                      <div className="tp-skills">
                        {technician.childcategories.map((child, index) => (
                          <span key={`child-${index}`} className="tp-skill-tag tp-childcategory-tag">{child.childCategoryName}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {!technician.categories?.length &&
                    !technician.subcategories?.length &&
                    !technician.childcategories?.length && (
                      <p className="tp-no-reviews">No skills listed</p>
                    )}
                </div>
              </div>
              <div className="tp-section-card">
                <h3 className="tp-section-title">Reviews</h3>
{reviews.length > 0 ? reviews.map((review) => (
  <div className="tp-review" key={review.id}>
    <div className="tp-review-header">
      <div className="tp-review-user-wrap">
        {review.userImage && (
          <img
            src={review.userImage}
            alt={review.userName}
            className="tp-review-avatar"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <div className="tp-review-user-info">
          <span className="tp-review-user">{review.userName}</span>
          {review.createdAt && (
            <span className="tp-review-date">
              {new Date(review.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </span>
          )}
        </div>
      </div>
      <p className="tp-review-rating">
        <img src={star} alt="star" className="tp-review-star" />
        {review.rating.toFixed(1)}
      </p>
    </div>

    {review.text && <p className="tp-review-text">"{review.text}"</p>}

    {/* Optional: show sub-ratings */}
    <div className="tp-review-subratings">
      <span>Work Quality: {review.workQualityRating.toFixed(1)}</span>
      <span>Behavior: {review.behaviorRating.toFixed(1)}</span>
      <span>Personality: {review.personalityRating.toFixed(1)}</span>
    </div>

    {review.wouldRecommend && (
      <span className="tp-review-recommend">✓ Would recommend</span>
    )}
  </div>
)) : <p className="tp-no-reviews">No reviews yet</p>}
              </div>
            </div>
          ) : (
            <div className="tp-section-card tp-section-scroll">
              <div className="tp-content-heading tp-work-heading">
                <span className="tp-content-eyebrow">Portfolio</span>
                <h1>Recent work</h1>
                <p>Projects and jobs completed by {technician.name}.</p>
              </div>
            <div className="tp-post-feed-container">
              <div className="tp-post-feed">
                {posts && posts.length > 0 ? (
                  posts.map(post => (
                    <div className="tp-post-card" key={post.id}>
                      <div className="tp-post-header">
                        <img
                          src={technician.profileImage}
                          alt=""
                          className="tp-post-avatar"
                          onError={(e) => { e.currentTarget.src = profile; }}
                        />
                        <div className="tp-post-info">
                          <strong className="tp-post-name">{technician.name}</strong>
                          <span className="tp-post-date">
                            {[post.date, post.time].filter(Boolean).join(' • ')}
                          </span>
                        </div>
                      </div>

                      {post.title && <h2 className="tp-post-title">{post.title}</h2>}
                      {post.text && <WorkGalleryDescription text={post.text} />}

                      {post.media.length > 0 && (
                        <div className={getMediaGridClass(post.media.length)}>
                          {post.media.slice(0, 5).map((media, idx) => (
                            <div className="tp-post-media" key={`${media.type}-${idx}`}>
                              {media.type === 'video' ? (
                                <video
                                  className="tp-post-video"
                                  controls
                                  playsInline
                                  preload="metadata"
                                  aria-label={`${post.title || 'Work'} video ${idx + 1}`}
                                >
                                  <source src={media.url} />
                                  Your browser does not support video playback.
                                </video>
                              ) : (
                                <img
                                  src={media.url}
                                  alt={`${post.title || 'Work'} ${idx + 1}`}
                                  className="tp-post-image"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              )}
                              {idx === 4 && post.media.length > 5 && (
                                <span className="tp-post-media-overflow">
                                  +{post.media.length - 5}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="tp-no-posts">No recent work posts available</p>
                )}
              </div>
            </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default TechProfile;